import { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Spinner,
} from 'reactstrap';
import { Btn } from '../../../AbstractElements';
import { toast } from 'react-toastify';
import PermissionService from '../../../Api/services/permission.service';
import type { components } from '../../../Api/types/openapi';
import { useTranslation } from 'react-i18next';

type Permission = components['schemas']['Permission'];
type UpdatePermission = components['schemas']['UpdatePermission'];

interface PermissionFormModalProps {
  isOpen: boolean;
  toggle: () => void;
  permission: Permission | null;
  onSuccess: () => void;
}

const PermissionFormModal: React.FC<PermissionFormModalProps> = ({
  isOpen,
  toggle,
  permission,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
  });

  useEffect(() => {
    if (isOpen && permission) {
      setFormData({
        name: permission.name || '',
      });
    }
  }, [isOpen, permission]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.name.trim()) {
      toast.error(t('El nombre es requerido'));
      return;
    }

    if (!permission?.id) {
      toast.error(t('ID de permiso no encontrado'));
      return;
    }

    try {
      setLoading(true);
      const updateData: UpdatePermission = {
        id: permission.id,
        name: formData.name.trim(),
      };
      await PermissionService.updatePermission(updateData);
      toast.success(t('Permiso actualizado exitosamente'));
      toggle();
      onSuccess();
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || t('Error al guardar permiso');
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        {t('Editar Permiso')}
      </ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          <FormGroup>
            <Label>{t('Nombre')} *</Label>
            <Input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder={t('Nombre del permiso')}
              required
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Btn color="secondary" onClick={toggle} disabled={loading}>
            {t('Cancelar')}
          </Btn>
          <Btn color="primary" type="submit" disabled={loading}>
            {loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                {t('Guardando...')}
              </>
            ) : (
              t('Aceptar')
            )}
          </Btn>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default PermissionFormModal;

